/**
 * Script library types (issue #86 phase 1).
 * Mirrors apps/api/src/routes/script-library.ts.
 */

export type ScriptClassification = 'read_only' | 'remediation';
export type ScriptStatus = 'draft' | 'approved' | 'disabled';

export interface ScriptParameter {
  name: string;
  label?: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
}

export interface ScriptLibraryEntry {
  id: string;
  name: string;
  problemClass: string;
  description: string;
  classification: ScriptClassification;
  language: string;
  body: string;
  parameters: ScriptParameter[];
  version: number;
  status: ScriptStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScriptLibraryListResponse {
  items: ScriptLibraryEntry[];
}

export interface ScriptLibraryForTicketResponse {
  problemClass: string | null;
  label: string | null;
  items: ScriptLibraryEntry[];
}

export const PROBLEM_CLASS_LABELS: Record<string, string> = {
  disk_space: 'Disk space',
  slow_performance: 'Slow performance',
  network_drop: 'Network / connectivity',
  email_delivery: 'Email delivery',
  printer: 'Printer',
  login_auth: 'Login / authentication',
  backup_failure: 'Backup failure',
  malware_alert: 'Malware alert',
  vpn: 'VPN',
};

export const PROBLEM_CLASSES = Object.keys(PROBLEM_CLASS_LABELS);

/**
 * Substitute the affected user into <USER> placeholders at copy time. Only
 * plain email/username characters are substituted (same rule as the curated
 * template library); anything else leaves the placeholder untouched.
 */
const SAFE_USER_RE = /^[A-Za-z0-9._@+-]{1,128}$/;

export function renderScriptBody(body: string, user?: string | null): string {
  if (!user || !SAFE_USER_RE.test(user)) return body;
  return body.split('<USER>').join(user);
}

/** Copy text to the clipboard, with a textarea fallback for older browsers. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}
